import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, ViewChild, ViewEncapsulation,
} from '@angular/core';
import {
  CompanyProfileInsight, InsightBar, InsightRecord, insightAmount, insightBars, insightBreakdown, insightLikelihood,
  insightListDetail, insightListItems, insightListText, insightNoteDetail, insightRecords, insightValueText,
  PROFILE_INSIGHT_CARD_LIST_HEADER_COLOUR, PROFILE_INSIGHT_CHART_COLOURS, PROFILE_INSIGHT_GAUGE_COLOUR,
} from './profile-insight';

// Renders one Magic Insights answer field by display type. The host carries the visual's class so a card can
// size it, and the same renderer shows the full answer inside the evidence dialog when [expanded] is set.
// Encapsulation is off so the rules can reach into the aira-* chart components, as the Logistics skin did.
@Component({
  selector: 'company-profile-insight-answer',
  templateUrl: './company-profile-insight-answer.component.html',
  styleUrls: ['./company-profile-insight-answer.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CompanyProfileInsightAnswerComponent implements OnChanges, AfterViewInit {
  @Input({ required: true }) insight!: CompanyProfileInsight;
  /** Show every list item and the full text, as in the evidence dialog. */
  @Input() expanded = false;
  @Input() listLimit = 4;
  @Input() chipLimit = 8;
  @Input() chartColours = PROFILE_INSIGHT_CHART_COLOURS;
  @Input() gaugeColour = PROFILE_INSIGHT_GAUGE_COLOUR;
  @Input() cardListHeaderColour = PROFILE_INSIGHT_CARD_LIST_HEADER_COLOUR;

  @ViewChild('textBlock') textBlock?: ElementRef<HTMLElement>;
  listExpanded = false;
  textExpanded = false;
  /** Whether the clamped summary hides text at the current width, so "Read full summary" is worth showing. */
  textOverflows = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.measureText();
  }

  @HostListener('window:resize')
  measureText(): void {
    const element = this.textBlock?.nativeElement;
    if (!element || this.textExpanded) return;
    const overflows = element.scrollHeight > element.clientHeight + 1;
    if (overflows !== this.textOverflows) {
      this.textOverflows = overflows;
      this.cdr.detectChanges();
    }
  }
  records: InsightRecord[] = [];
  bars: InsightBar[] = [];

  readonly listText = insightListText;
  readonly listDetail = insightListDetail;
  readonly valueText = insightValueText;
  readonly noteDetail = insightNoteDetail;

  ngOnChanges(): void {
    const { displayType, value, schema, key } = this.insight;
    this.records = displayType === 'records' ? insightRecords(value, schema) : [];
    this.bars = displayType === 'bars' ? insightBars(value, schema, key)
      : displayType === 'breakdown' ? insightBreakdown(value, schema) : [];
  }

  get items(): any[] {
    return insightListItems(this.insight.value);
  }

  /** Empty lists, and bar charts where every value is zero, read as an empty state rather than blank bars. */
  get isEmptyList(): boolean {
    if (!Array.isArray(this.insight.value)) return false;
    return !this.insight.value.length || (this.insight.displayType === 'bars' && this.bars.every(bar => bar.empty));
  }

  get amount() {
    return insightAmount(this.insight.value, this.insight.schema);
  }

  get likelihood() {
    return insightLikelihood(this.insight.value, this.insight.schema, this.insight.key);
  }

  get text(): string {
    return typeof this.insight.value === 'string' ? this.insight.value.trim() : insightValueText(this.insight.value);
  }

  /** Short strings, such as a maturity classification, read as a headline rather than a paragraph. */
  get isStatement(): boolean {
    return this.text.length <= 48;
  }


  visible<T>(list: T[], limit: number): T[] {
    return this.expanded || this.listExpanded ? list : list.slice(0, limit);
  }

  toggleList(): void {
    this.listExpanded = !this.listExpanded;
  }
}
